/**
 * ProjectService
 * Service for Project entity operations
 * Route: api/projects
 * Controller: ProjectsController.cs
 */
import { fetchWorkItemsByIds, cleanWorkItemIds } from '../base/workItemsByIds';
import { CrudService } from '../base/CrudService';
/**
 * ProjectService
 * Handles project CRUD operations and custom endpoints
 * Maps to /api/projects endpoint
 */
export class ProjectService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? ProjectService.DEFAULT_SERVICE_PATH,
            basePath: ProjectService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CUSTOM METHODS ====================
    /**
     * Get project with enriched model view by ID
     * GET /api/projects/view/{id}
     * @param id - Project ID
     * @returns Enriched project model view
     */
    async getViewById(id) {
        const response = await this.getViewByIdRaw(id);
        if (!response.data) {
            throw new Error(`Project view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get project view by ID (raw)
     * @param id - Project ID
     * @returns ResponseData with enriched model view
     */
    getViewByIdRaw(id) {
        const endpoint = this.getEndpoint('view', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get filtered list of project views (enriched with lookup data)
     * POST /api/projects/list-view
     * @param filter - Optional filter criteria
     * @returns Array of enriched project model views
     */
    async getListView(filter) {
        const response = await this.getListViewRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get filtered list of project views (raw)
     * @param filter - Optional filter criteria
     * @returns ResponseData with array of enriched model views
     */
    getListViewRaw(filter) {
        const endpoint = this.getEndpoint('listView', 'list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter || {});
    }
    /**
     * Get filtered list of project views for current user (projects where user is a member)
     * POST /api/projects/my-list-view
     * @param filter - Optional filter criteria
     * @returns Array of enriched project model views for current user
     */
    async getMyListView(filter) {
        const response = await this.getMyListViewRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get my project views (raw)
     * @param filter - Optional filter criteria
     * @returns ResponseData with array of enriched model views
     */
    getMyListViewRaw(filter) {
        const endpoint = this.getEndpoint('myListView', 'my-list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter || {});
    }
    /**
     * Create new project using CreateProjectViewModel
     * POST /api/projects/create
     * @param model - Project creation data
     * @returns Created project
     */
    async createFromViewModel(model) {
        const response = await this.createFromViewModelRaw(model);
        if (!response.data) {
            throw new Error('Failed to create project');
        }
        return response.data;
    }
    /**
     * Create project from view model (raw)
     * @param model - Project creation data
     * @returns ResponseData with created project
     */
    createFromViewModelRaw(model) {
        const endpoint = this.getEndpoint('createFromViewModel', 'create');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, model);
    }
    /**
     * Update project using UpdateProjectViewModel
     * PATCH /api/projects/{id}
     * @param id - Project ID
     * @param model - Project update data
     * @returns Updated project
     */
    async updateFromViewModel(id, model) {
        const response = await this.updateFromViewModelRaw(id, model);
        if (!response.data) {
            throw new Error(`Failed to update project with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Update project from view model (raw)
     * @param id - Project ID
     * @param model - Project update data
     * @returns ResponseData with updated project
     */
    updateFromViewModelRaw(id, model) {
        const endpoint = this.getEndpoint('updateFromViewModel', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.patch(url, model);
    }
    /**
     * Invalidate project cache
     * POST /api/projects/cache/invalidate
     * @returns Success status
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.data ?? false;
    }
    /**
     * Invalidate cache (raw)
     * @returns ResponseData with success status
     */
    invalidateCacheRaw() {
        const endpoint = this.getEndpoint('invalidateCache', 'cache/invalidate');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
    // ==================== BY IDS (Events hub Stage 2) ====================
    /**
     * POST api/projects/by-ids: live summaries of several projects with the caller's own visibility.
     * Projects the caller may not see come back with isRestricted = true and no details.
     * Ids are de-duplicated and sent in batches of 100 (the server's limit).
     * @param ids - Project ids
     * @returns One WorkItemSummary per project the server knows
     */
    async getByIds(ids) {
        return fetchWorkItemsByIds(this.http, this.buildUrl('by-ids'), ids, 'projects');
    }
    /**
     * POST api/projects/by-ids (raw, one call of at most 100 ids)
     * @param ids - Project ids
     * @returns ResponseData with the summaries
     */
    getByIdsRaw(ids) {
        return this.http.post(this.buildUrl('by-ids'), { ids: cleanWorkItemIds(ids) });
    }
}
ProjectService.DEFAULT_SERVICE_PATH = 'projects';
ProjectService.BASE_PATH = 'api/projects';
//# sourceMappingURL=ProjectService.js.map